/**
 * ServiceNowConnectionForm — "Add ServiceNow Connection".
 *
 * Registers a ServiceNow connection (instance + credential) so LeftShield can create/sync
 * incidents. `POST` via `useCreateServiceNowConnection()`.
 *
 * Pattern: `react-hook-form` + `zodResolver`, schema imported from
 * `ServiceNowConnection.schema.ts` (never redefined), Fluent `Field` for verbatim validation
 * messages, server errors mapped via the shared `applyServerErrors`
 * (Charter M3 / form-standard.context.md). The outer `Dialog` chrome is owned by
 * `IntegrationsPage.tsx` (this feature's own `components/`, not `src/pages/**`) — this component
 * renders the form CONTENT only and keeps its existing `onSuccess`/`onCancel` prop contract so the
 * hosting page needs no change.
 */
import type { FC } from 'react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Field, Input, Select, makeStyles, tokens } from '@fluentui/react-components';
import { PlugConnectedAddRegular } from '@fluentui/react-icons';
import { useCreateServiceNowConnection } from '../hooks';
import type { CreateServiceNowConnectionRequest } from '../types';
import { applyServerErrors, useReportFormDirty, FormActions, ServerErrorBanner } from '../../../components/forms';
import { SERVICENOW_AUTH_TYPE_OPTIONS } from './connectionFormFields';
import {
  ServiceNowConnectionSchema,
  type ServiceNowConnectionFormValues,
  type ServiceNowConnectionFormOutput,
} from './ServiceNowConnection.schema';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
});

// Known request-DTO field keys — used by the server-error -> field mapping.
const KNOWN_FIELDS = ['name', 'instanceUrl', 'authType', 'username', 'password'] as const;

const DEFAULT_VALUES: ServiceNowConnectionFormValues = {
  name: '',
  instanceUrl: '',
  authType: 'Basic',
  username: '',
  password: '',
};

export interface ServiceNowConnectionFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  /** Reports dirty state up so the hosting dialog can guard an unsaved-changes dismiss. */
  onDirtyChange?: (dirty: boolean) => void;
}

export const ServiceNowConnectionForm: FC<ServiceNowConnectionFormProps> = ({
  onSuccess,
  onCancel,
  onDirtyChange,
}) => {
  const styles = useStyles();

  const {
    control,
    handleSubmit,
    setError,
    reset,
    formState: { errors, isDirty },
  } = useForm<ServiceNowConnectionFormValues, unknown, ServiceNowConnectionFormOutput>({
    resolver: zodResolver(ServiceNowConnectionSchema),
    mode: 'onTouched',
    defaultValues: DEFAULT_VALUES,
  });

  useReportFormDirty(isDirty, onDirtyChange);

  const [globalError, setGlobalError] = useState<string | undefined>();

  const createConnection = useCreateServiceNowConnection();

  const onValid = (values: ServiceNowConnectionFormOutput) => {
    // Guard double-submit.
    if (createConnection.isPending) return;
    setGlobalError(undefined);

    const request: CreateServiceNowConnectionRequest = {
      name: values.name,
      instanceUrl: values.instanceUrl,
      authType: values.authType,
      username: values.username,
      password: values.password,
    };

    createConnection.mutate(request, {
      onSuccess: () => {
        reset(DEFAULT_VALUES);
        onSuccess?.();
      },
      onError: (error) => {
        const { globalMessage } = applyServerErrors(error, KNOWN_FIELDS, setError);
        setGlobalError(globalMessage);
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onValid)} className={styles.form} noValidate>
      <ServerErrorBanner title="Error" message={globalError} testId="servicenow-connection-form-server-error" />

      <Field
        label="Connection Name"
        required
        validationState={errors.name ? 'error' : undefined}
        validationMessage={errors.name?.message}
      >
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="servicenow-connection-form-name"
              value={field.value ?? ''}
              placeholder="My ServiceNow Instance"
              disabled={createConnection.isPending}
            />
          )}
        />
      </Field>

      <Field
        label="Instance URL"
        required
        validationState={errors.instanceUrl ? 'error' : undefined}
        validationMessage={errors.instanceUrl?.message}
        hint="e.g., https://your-instance.service-now.com"
      >
        <Controller
          name="instanceUrl"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="servicenow-connection-form-instanceUrl"
              value={field.value ?? ''}
              placeholder="https://your-instance.service-now.com"
              disabled={createConnection.isPending}
            />
          )}
        />
      </Field>

      <Field label="Authentication Type" required>
        <Controller
          name="authType"
          control={control}
          render={({ field }) => (
            <Select
              value={field.value}
              onChange={(_, data) => field.onChange(data.value)}
              onBlur={field.onBlur}
              disabled={createConnection.isPending}
              data-testid="servicenow-connection-form-authType"
            >
              {SERVICENOW_AUTH_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          )}
        />
      </Field>

      <Field
        label="Username"
        required
        validationState={errors.username ? 'error' : undefined}
        validationMessage={errors.username?.message}
        hint="ServiceNow user with API access"
      >
        <Controller
          name="username"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="servicenow-connection-form-username"
              value={field.value ?? ''}
              placeholder="admin"
              disabled={createConnection.isPending}
            />
          )}
        />
      </Field>

      <Field
        label="Password"
        required
        validationState={errors.password ? 'error' : undefined}
        validationMessage={errors.password?.message}
        hint="User password or OAuth client secret"
      >
        <Controller
          name="password"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              type="password"
              data-testid="servicenow-connection-form-password"
              value={field.value ?? ''}
              placeholder="Your password"
              disabled={createConnection.isPending}
            />
          )}
        />
      </Field>

      <FormActions
        mode="create"
        testIdPrefix="servicenow-connection-form"
        isSubmitting={createConnection.isPending}
        onCancel={onCancel}
        submitLabel="Create Connection"
        submittingLabel="Creating…"
        submitIcon={<PlugConnectedAddRegular />}
      />
    </form>
  );
};
